import API from './api/index';
import appMode from './appMode';

// Default test contact ID for development
const DEFAULT_TEST_CONTACT_ID = 1146;

class StudioDataService {
  constructor() {
    this.contactId = null;
    this.designData = null;
    this.inputDetails = null;
    /** Full raw API response — used to populate contactDataStore */
    this.rawApiData = null;
    /** Panel + inverter selection made in PANEL_SETTINGS_STATE */
    this.panelSettings = null;
  }

  // Initialize or load design by contact ID
  async initializeDesign(contactId = null) {
    try {
      this.contactId = contactId || DEFAULT_TEST_CONTACT_ID;

      // Try to load existing data from API
      // Retry on Network Error — the ArkaGo Bridge XHR interceptor may not be
      // ready when the portal fires its first request immediately on init.
      try {
        const apiData = await this._loadFinancialDataWithRetry(this.contactId);
        
        // ALWAYS set rawApiData first (needed for contactDataStore population)
        this.rawApiData = apiData;
        this.inputDetails = apiData.input_details || {};

        if (apiData) {
          // mapApiResponseToDesignData handles both cases:
          // - design_details present → restores saved design
          // - design_details absent  → creates default design using input_details lat/lng
          this.designData = this.mapApiResponseToDesignData(apiData);
          return this.designData;
        }
      } catch (loadError) {
        console.warn('[StudioDataService] No existing design found, starting fresh:', loadError.message);
      }
      
      // No API data at all (404) — create default design
      this.designData = this.getDefaultDesignData();

      // Apply ArkaGo URL lat/lng/zoom params to the new design's mapData
      const urlLoc = appMode.getLocationParams();
      if (urlLoc) {
        this.designData.mapData.latitude  = urlLoc.lat;
        this.designData.mapData.longitude = urlLoc.lng;
        this.designData.mapData.zoom      = urlLoc.zoom;
      }

      return this.designData;
    } catch (error) {
      console.error('❌ [STUDIO_SERVICE] Initialize failed:', error);
      this.designData = this.getDefaultDesignData();
      return this.designData;
    }
  }

  // Retry LOAD on Network Error — the ArkaGo WebView bridge may not be
  // ready when the portal fires its very first XHR.
  async _loadFinancialDataWithRetry(contactId, maxRetries = 3, baseDelayMs = 600) {
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const response = await API.STUDIO_API.FINANCIAL_DATA.LOAD(contactId);
        if (attempt > 0) {
          console.log(`[StudioDataService] LOAD succeeded on attempt ${attempt + 1}`);
        }
        return response.data;
      } catch (error) {
        const isNetworkError = !error.response && error.message === 'Network Error';
        if (isNetworkError && attempt < maxRetries) {
          const delay = baseDelayMs * (attempt + 1);
          console.warn(`[StudioDataService] LOAD attempt ${attempt + 1} failed (Network Error), retrying in ${delay}ms...`);
          await new Promise(resolve => setTimeout(resolve, delay));
          continue;
        }
        throw error;
      }
    }
  }

  // Map API response to internal design data structure
  mapApiResponseToDesignData(apiData) {
    const designDetails = apiData.design_details || {};
    const inputDetails = apiData.input_details || {};

    // If design_details has proper structure, use it
    if (designDetails.stageData || designDetails.mapData) {
      const mapped = {
        ...this.getDefaultDesignData(),
        ...designDetails,
        infoFormData: this.mapInputDetailsToInfoForm(inputDetails)
      };
      return mapped;
    }
    
    // Otherwise create default structure
    return {
      ...this.getDefaultDesignData(),
      infoFormData: this.mapInputDetailsToInfoForm(inputDetails),
      mapData: {
        ...this.getDefaultDesignData().mapData,
        latitude: inputDetails.latitude || 28.5965884,
        longitude: inputDetails.longitude || 77.082604
      }
    };
  }

  // Map API input_details to internal infoFormData
  mapInputDetailsToInfoForm(inputDetails) {
    return {
      floorValue: inputDetails.floor_value || 5,
      supply_phase: inputDetails.phase === 'single' ? 'Single Phase' : 'Three Phase',
      tariff_rate: inputDetails.tariff_rate || 7,
      monthly_bill: inputDetails.monthly_bill || 2000,
      sanction_load: inputDetails.sanctioned_load || 5,
      pincode: inputDetails.pincode || '',
      address: inputDetails.address || ''
    };
  }

  // Map internal infoFormData to API input_details format
  mapInfoFormToInputDetails(infoFormData) {
    return {
      floor_value: infoFormData.floorValue || 1,
      monthly_bill: infoFormData.monthly_bill || 1000,
      sanctioned_load: infoFormData.sanction_load || 3,
      tariff_rate: infoFormData.tariff_rate || 10,
      phase: infoFormData.supply_phase === 'Single Phase' ? 'single' : 'three',
      pincode: infoFormData.pincode || '',
      address: infoFormData.address || '',
      latitude: this.designData?.mapData?.latitude || 28.5965884,
      longitude: this.designData?.mapData?.longitude || 77.082604
    };
  }

  // Save design data to API
  async saveDesign() {
    try {
      if (!this.contactId) {
        throw new Error('No contact ID set');
      }

      // Clean up stageData before saving to prevent data accumulation
      const cleanedStageData = this.cleanStageData(this.designData?.stageData || {});

      // Prepare design_details payload
      const designDetails = {
        currentAppState: this.designData?.currentAppState || 'FLOOR_STATE',
        mapData: this.designData?.mapData || {},
        stageData: cleanedStageData,
        componentData: this.designData?.componentData || {},
        ...(this.panelSettings ? { panelSettings: this.panelSettings } : {}),
        updatedAt: new Date().toISOString()
      };

      const response = await API.STUDIO_API.FINANCIAL_DATA.SAVE(
        this.contactId,
        designDetails
      );

      this.designData.updatedAt = new Date().toISOString();
      return response.data;
    } catch (error) {
      console.error('[STUDIO_SERVICE] Save failed:', error);
      throw error;
    }
  }

  // Clean stage data to remove duplicates and invalid entries
  cleanStageData(stageData) {
    const cleaned = { ...stageData };

    // Clean obstructions - remove duplicates and invalid entries
    if (cleaned.obstructionData && Array.isArray(cleaned.obstructionData)) {
      const uniqueObstructions = [];
      const seenObstructions = new Set();
      let removedCount = 0;

      cleaned.obstructionData.forEach(obs => {
        // Skip obstructions with (0,0) centerPoint - these are unpositioned/invalid
        const x = obs.centerPoint?.x ?? 0;
        const y = obs.centerPoint?.y ?? 0;
        
        // Filter out obstructions at origin (0,0) - likely unpositioned
        if (x === 0 && y === 0) {
          removedCount++;
          return;
        }
        
        // Create unique key based on type, centerPoint, scale, and baseHeight
        const key = `${obs.type}_${x.toFixed(2)}_${y.toFixed(2)}_${obs.scale}_${obs.baseHeight}`;
        
        if (!seenObstructions.has(key)) {
          seenObstructions.add(key);
          uniqueObstructions.push(obs);
        } else {
          removedCount++;
        }
      });

      cleaned.obstructionData = uniqueObstructions;
    }

    // Clean ground data — remove invalid polygons and optimise subarray payload
    if (cleaned.groundData && Array.isArray(cleaned.groundData.children)) {
      cleaned.groundData.children = cleaned.groundData.children
        .filter(child => {
          if (child === null) return false;
          if (child.type !== 'PolygonModel') return true;
          const allZero = child.outlinePoints?.every(p =>
            p[0] === 0 && p[1] === 0 && p[2] === 0
          );
          return !allZero;
        })
        .map(polygon => {
          if (polygon.type !== 'PolygonModel' || !Array.isArray(polygon.children)) return polygon;
          // Keep full subarray children (including subarrayMap and panelProperties)
          // so Subarray.loadObject() can reconstruct panels on load.
          return polygon;
        });
    }

    return cleaned;
  }

  // Get default design data structure
  getDefaultDesignData() {
    return {
      currentAppState: 'FLOOR_STATE',
      mapData: {
        latitude: 28.5965884,
        longitude: 77.082604,
        zoom: 20,
        address: '',
        imageUrl: null,
        imageDimensions: { width: 0, height: 0 }
      },
      stageData: {
        groundData: { children: [] },
        obstructionData: [],
        floorValue: 5,
        modelId: 1,
        obsId: 1,
        _SUBARRAY_ID_INIT: 0,
        availableArea: 0,
        panel_map: [],
        design_dcsize: 0,
        design_acsize: 0
      },
      infoFormData: {
        floorValue: 5,
        supply_phase: 'Single Phase',
        tariff_rate: 7,
        monthly_bill: 2000,
        sanction_load: 5,
        pincode: '',
        address: ''
      },
      componentData: {},
      updatedAt: new Date().toISOString()
    };
  }

  // Get current design data
  getDesignData() {
    return this.designData;
  }

  // Update design data
  updateDesignData(updates) {
    this.designData = {
      ...this.designData,
      ...updates,
      updatedAt: new Date().toISOString()
    };
  }

  // Get stage data
  getStageData() {
    if (!appMode.isAppMode()) {
      const raw = localStorage.getItem('stageData');
      return raw ? JSON.parse(raw) : this.getDefaultDesignData().stageData;
    }
    return this.designData?.stageData || this.getDefaultDesignData().stageData;
  }

  // Set stage data
  setStageData(stageData) {
    if (!appMode.isAppMode()) {
      localStorage.setItem('stageData', JSON.stringify(stageData));
      return;
    }
    this.updateDesignData({ stageData });
  }

  // Get map data
  getMapData() {
    return this.designData?.mapData || this.getDefaultDesignData().mapData;
  }

  // Set map data
  setMapData(mapData) {
    this.updateDesignData({ mapData });
  }

  // Get info form data
  getInfoFormData() {
    return this.designData?.infoFormData || this.getDefaultDesignData().infoFormData;
  }

  // Set info form data
  setInfoFormData(infoFormData) {
    this.updateDesignData({ infoFormData });
  }

  // Get component data
  getComponentData() {
    return this.designData?.componentData || {};
  }

  // Set component data
  setComponentData(componentData) {
    this.updateDesignData({ componentData });
  }

  // Get current app state
  getCurrentAppState() {
    return this.designData?.currentAppState || 'FLOOR_STATE';
  }

  // Check if this is a new design (no saved design_details from API)
  isNewDesign() {
    const designDetails = this.rawApiData?.design_details || {};
    // New design if no stageData and no mapData in design_details
    return !designDetails.stageData && !designDetails.mapData;
  }

  // Set current app state
  setCurrentAppState(currentAppState) {
    if (!appMode.isAppMode()) {
      localStorage.setItem('currentAppState', currentAppState);
      return;
    }
    this.updateDesignData({ currentAppState });
  }

  // Panel settings (selected panel/inverter from PANEL_SETTINGS_STATE)
  setPanelSettings(settings) {
    this.panelSettings = settings;
  }

  getPanelSettings() {
    return this.panelSettings || this.designData?.panelSettings || null;
  }

  // Get contact ID
  getContactId() {
    return this.contactId;
  }

  // Set contact ID
  setContactId(contactId) {
    this.contactId = contactId;
  }
}

export default new StudioDataService();
